/**
 * AdminFinancialAddonTable - Tabela de receita agrupada por add-on
 * Exibe nome do addon, preço unitário, quantidade total, subtotal e % do MRR de addons
 * Filtro Todos/Ativos para alternar entre dados de todos os tenants ou somente ativos
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import type { AddonRevenue } from '@/hooks/useAdminFinancial';

/** Formata valores monetários em BRL */
const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

interface Props {
  addonRevenues: AddonRevenue[];
  mrrAddons: number;
  /** Receita por addon incluindo todos os tenants (active + trialing + past_due) */
  allAddonRevenues: AddonRevenue[];
  /** MRR total de addons incluindo todos os tenants */
  mrrAddonsAll: number;
}

export function AdminFinancialAddonTable({ addonRevenues, mrrAddons, allAddonRevenues, mrrAddonsAll }: Props) {
  /** Filtro ativo: 'ativos' mostra somente pagantes, 'todos' inclui past_due */
  const [filter, setFilter] = useState<'ativos' | 'todos'>('ativos');

  /** Dados exibidos conforme filtro selecionado */
  const data = filter === 'ativos' ? addonRevenues : allAddonRevenues;
  const totalMrr = filter === 'ativos' ? mrrAddons : mrrAddonsAll;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Receita por Add-on</CardTitle>
          {/* Toggle de filtro Todos / Ativos */}
          <div className="flex gap-1">
            <Button
              variant={filter === 'todos' ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setFilter('todos')}
            >
              Todos
            </Button>
            <Button
              variant={filter === 'ativos' ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setFilter('ativos')}
            >
              Ativos
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Add-on</TableHead>
              <TableHead className="text-right">Preço Unit.</TableHead>
              <TableHead className="text-center">Qtd Total</TableHead>
              <TableHead className="text-right">Subtotal</TableHead>
              <TableHead className="text-right">% MRR</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((addon) => (
              <TableRow key={addon.slug}>
                <TableCell className="font-medium">{addon.addon_name}</TableCell>
                <TableCell className="text-right">{formatBRL(addon.price_monthly)}</TableCell>
                <TableCell className="text-center">{addon.total_quantity}</TableCell>
                <TableCell className="text-right font-medium">{formatBRL(addon.subtotal)}</TableCell>
                <TableCell className="text-right text-muted-foreground">{addon.percentage.toFixed(1)}%</TableCell>
              </TableRow>
            ))}
            {/* Linha de total */}
            <TableRow className="bg-muted/50 font-bold">
              <TableCell>Total</TableCell>
              <TableCell />
              <TableCell className="text-center">
                {data.reduce((s, a) => s + a.total_quantity, 0)}
              </TableCell>
              <TableCell className="text-right">{formatBRL(totalMrr)}</TableCell>
              <TableCell className="text-right">100%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
